/* Independent case-gift controller. Does not alter pn-gift.js or discount rules.
   Shopify handles all prices/discounts. Only native zero-price variants can be added. */
(() => {
  if (window.pnCaseGift) return;
  const config = window.pnCaseGiftConfig;
  const marker = '_pn_case_gift_campaign';
  const root = window.Shopify?.routes?.root || '/';
  let queue = Promise.resolve(), scheduled, installed = false;
  const known = () => config.tiers.map(tier => tier.selectedVariantId);
  function configured() {
    return config?.version === 1 && config.enabled === true && typeof config.approvalReference === 'string' && config.approvalReference.trim() &&
      ((config.validityMode === 'source-runtime-no-date-check' && typeof config.sourceVerifiedAt === 'string' && config.startsAt === null && config.endsAt === null) ||
        (config.validityMode === 'approved-schedule' && Number.isFinite(Date.parse(config.startsAt)) &&
        ((Number.isFinite(Date.parse(config.endsAt)) && Date.parse(config.endsAt) > Date.parse(config.startsAt)) || (config.endsAt === null && config.noExpiryApproved === true)))) &&
      config.quantityPerCart === 1 && config.countingMode === 'source-excluded-variants' && Array.isArray(config.excludedVariantIds) && config.excludedVariantIds.length === 4 &&
      config.tiers?.length === 3 && config.tiers.every(tier => tier.variants.some(v => v.variantId === tier.selectedVariantId));
  }
  function active(now = Date.now()) {
    return configured() && (config.validityMode === 'source-runtime-no-date-check' || (now >= Date.parse(config.startsAt) && (config.endsAt === null || now < Date.parse(config.endsAt))));
  }
  function plan(cart, now = Date.now()) {
    if (!Array.isArray(cart?.items)) throw new Error('Invalid cart');
    if (!configured()) return {blocked: true, count: 0, desired: null, owned: []};
    const count = cart.items.reduce((sum, item) => sum + (!config.excludedVariantIds.includes(item.variant_id) ? item.quantity : 0), 0);
    const tier = active(now) ? [...config.tiers].reverse().find(t => count >= t.minimumWatches) : null;
    const desired = tier ? {tier, variantId: tier.selectedVariantId} : null;
    const owned = cart.items.filter(i => known().includes(i.variant_id));
    return {blocked: false, count, desired, owned};
  }
  function feedback(message) {
    let el = document.querySelector('[data-pn-case-gift-status]');
    if (!el && message) {
      el = document.createElement('p'); el.dataset.pnCaseGiftStatus = ''; el.setAttribute('role', 'status');
      const host = document.querySelector('cart-drawer .drawer__inner') || document.querySelector('.an-gift') || document.querySelector('main');
      host?.append(el);
    }
    if (el) el.textContent = message;
  }
  let rawFetch;
  async function request(path, data) {
    try {
      const response = await rawFetch(root + path, data ? {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(data)} : {});
      if (!response.ok) throw new Error();
      // Shopify also serves valid Ajax JSON as text/javascript (F-019).
      const value = await response.json();
      if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error();
      return value;
    } catch (_) { throw new Error('The complimentary case could not be updated. Please try again.'); }
  }
  async function synchronize() {
    if (!configured()) return;
    let changed = false, result;
    try {
      let cart = await request('cart.js');
      let next = plan(cart);
      if (next.blocked) return;
      // Validate target variant before replacing an existing case. No charged fallback.
      if (next.desired && !next.owned.some(i => i.variant_id === next.desired.variantId && i.quantity === 1)) {
        const product = await request('products/' + next.desired.tier.handle + '.js');
        const variant = product.variants?.find(v => v.id === next.desired.variantId);
        if (!variant?.available || variant.price !== 0) throw new Error('The complimentary case is currently unavailable.');
      }
      const updates = {};
      let retained = false;
      for (const item of next.owned) {
        if (next.desired && item.variant_id === next.desired.variantId && !retained) {
          retained = true; if (item.quantity !== 1) updates[item.key] = 1;
        } else updates[item.key] = 0;
      }
      const sections = ['cart-drawer', 'cart-icon-bubble'];
      if (Object.keys(updates).length) {
        result = await request('cart/update.js', {updates, sections, sections_url: location.pathname});
        changed = true;
      }
      if (next.desired && !retained) {
        // Re-read eligibility after a removal / any concurrent native cart change.
        cart = await request('cart.js');
        const current = plan(cart);
        if (current.desired?.variantId === next.desired.variantId && !current.owned.length) {
          result = await request('cart/add.js', {items: [{id: current.desired.variantId, quantity: 1, properties: {[marker]: config.campaignId}}], sections, sections_url: location.pathname});
          changed = true;
        }
      }
      if (changed) {
        const drawer = document.querySelector('cart-drawer');
        if (drawer?.renderContents && result?.sections) drawer.renderContents(result);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, {source: 'pn-case-gift'});
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
      feedback('');
    } catch (e) { feedback(e.message); }
  }
  function sync() { queue = queue.then(synchronize, synchronize); return queue; }
  function schedule() { clearTimeout(scheduled); scheduled = setTimeout(sync, 350); }
  function install() {
    // An unapproved configuration is inert: no fetch hooks, cart reads or writes.
    if (installed || !configured()) return;
    installed = true;
    rawFetch = window.fetch.bind(window); // Load AFTER pn-gift.js; preserves its observer.
    window.fetch = async (input, init) => {
      const response = await rawFetch(input, init);
      try {
        const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, location.href);
        if (url.origin === location.origin && /\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname) && response.ok) schedule();
      } catch (_) {}
      return response;
    };
    document.addEventListener('cart:refresh', schedule);
    document.addEventListener('shopify:section:load', schedule);
    window.addEventListener('pageshow', schedule);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) schedule(); });
    // One bounded deadline timer; never replace the source campaign date.
    if (config.endsAt) {
      const delay = Date.parse(config.endsAt) - Date.now();
      if (delay > 0 && delay < 2147483647) setTimeout(schedule, delay);
    }
    schedule();
  }
  window.pnCaseGift = {plan, active, configured, sync, install};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', install); else install();
})();
